import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, LockIcon, CheckCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { ModuleCard } from '../components/ModuleCard';
import { coursesStructure } from '../data/courseData';
import { coursesData } from '../data/coursesData';
import { LockedCourseModal } from '../components/LockedCourseModal';
import { PointsCard } from '../components/PointsCard';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../contexts/AuthContext';
export function Course() {
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const {
    getCourseProgress
  } = useProgress();
  const {
    unlockedAll
  } = useAuth();
  // Scroll to top when course changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [courseId]);
  // If no courseId in URL, redirect to courses overview
  if (!courseId) {
    return <Navigate to="/cursuri" replace data-id="element-8073" />;
  }
  // Find the specific course
  const courseInfo = coursesData.find(c => c.id === courseId);
  const detailedCourse = coursesStructure.find(c => c.id === courseId);
  // If course not found, redirect to courses overview
  if (!courseInfo) {
    return <Navigate to="/cursuri" replace data-id="element-8074" />;
  }
  const handleLockedCourseClick = (courseTitle: string) => {
    if (unlockedAll) return; // Don't show modal if all courses are unlocked
    setSelectedCourse(courseTitle);
    setShowLockedModal(true);
  };
  const totalLessons = courseInfo.lessonCount;
  // Override status if unlockedAll is true
  const isActive = courseInfo.status === 'active' || unlockedAll;
  const progress = isActive ? getCourseProgress(courseInfo.id, totalLessons) : 0;
  return <div className="w-full min-h-screen bg-[#F1F1F1]" data-id="element-8075">
      <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white py-16" data-id="element-8076">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8077">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-8078">
            <Link to="/cursuri" className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors mb-6" data-id="element-8079">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-8080" />
              <span data-id="element-8081">Înapoi la toate training-urile</span>
            </Link>
            <div className="flex items-center space-x-4 mb-4" data-id="element-8082">
              <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-semibold ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-8083">
                {isActive ? <>
                    <CheckCircleIcon className="w-4 h-4" data-id="element-8084" />
                    <span data-id="element-8085">Disponibil acum</span>
                  </> : <>
                    <LockIcon className="w-4 h-4" data-id="element-8086" />
                    <span data-id="element-8087">Necesită logare</span>
                  </>}
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-8088">
              {courseInfo.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl" data-id="element-8089">
              {courseInfo.description}
            </p>
            {isActive && <div className="mt-6 max-w-md" data-id="element-8090">
                <div className="flex items-center justify-between text-sm text-gray-300 mb-2" data-id="element-8091">
                  <span data-id="element-8092">Progres</span>
                  <span className="font-semibold" data-id="element-8093">{progress}%</span>
                </div>
                <div className="bg-white bg-opacity-20 rounded-full h-3 overflow-hidden" data-id="element-8094">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${progress}%`
              }} transition={{
                duration: 1,
                delay: 0.5
              }} className="bg-white h-full rounded-full" data-id="element-8095" />
                </div>
              </div>}
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8096">
        {/* Points Card */}
        <div className="mb-8" data-id="element-8097">
          <PointsCard compact data-id="element-8098" />
        </div>

        <div className="relative" data-id="element-8099">
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-[#357BEB] bg-opacity-20 hidden md:block" data-id="element-8100"></div>
          <div className="space-y-8" data-id="element-8101">
            {detailedCourse && isActive ?
          // Show detailed modules if course is active (or all unlocked) and has content
          detailedCourse.modules.map((module, index) => <motion.div key={module.id} initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="relative" data-id="element-8102">
                    <div className="hidden md:flex absolute left-0 w-16 h-16 rounded-full items-center justify-center text-white font-bold text-xl shadow-lg bg-[#357BEB]" data-id="element-8103">
                      {index + 1}
                    </div>
                    <div className="md:ml-24" data-id="element-8104">
                      <ModuleCard module={module} index={index} courseId={courseInfo.id} data-id="element-8105" />
                    </div>
                  </motion.div>) :
          // Show placeholder modules - unlocked if unlockedAll is true
          courseInfo.modules.map((moduleInfo, index) => <motion.div key={`${courseInfo.id}-module-${index}`} initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="relative" data-id="element-8106">
                    <div className={`hidden md:flex absolute left-0 w-16 h-16 rounded-full items-center justify-center text-white font-bold text-xl shadow-lg ${isActive ? 'bg-[#357BEB]' : 'bg-gray-400'}`} data-id="element-8107">
                      {index + 1}
                    </div>
                    <div className="md:ml-24" data-id="element-8108">
                      <div onClick={() => !isActive && handleLockedCourseClick(courseInfo.title)} className={`bg-white rounded-2xl p-6 shadow-md border border-gray-200 transition-all duration-300 ${!isActive ? 'opacity-60 hover:opacity-100 cursor-pointer' : ''}`} data-id="element-8109">
                        <div className="flex items-start space-x-4" data-id="element-8110">
                          <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${isActive ? 'bg-[#357BEB] bg-opacity-10' : 'bg-gray-200'}`} data-id="element-8111">
                            {isActive ? <CheckCircleIcon className="w-6 h-6 text-[#357BEB]" data-id="element-8112" /> : <LockIcon className="w-6 h-6 text-gray-400" data-id="element-8113" />}
                          </div>
                          <div className="flex-1" data-id="element-8114">
                            <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-8115">
                              {moduleInfo.title}
                            </h3>
                            {isActive && <p className="text-gray-600 text-sm mb-4" data-id="element-8116">
                                Conținutul acestui modul va fi disponibil în
                                curând.
                              </p>}
                            <div className="flex items-center justify-between" data-id="element-8117">
                              <span className="text-sm text-gray-500" data-id="element-8118">
                                {moduleInfo.lessonCount} lecții
                              </span>
                              <div className={`flex items-center space-x-2 font-medium text-sm ${isActive ? 'text-[#357BEB]' : 'text-gray-400'}`} data-id="element-8119">
                                {isActive ? <>
                                    <CheckCircleIcon className="w-4 h-4" data-id="element-8120" />
                                    <span data-id="element-8121">În dezvoltare</span>
                                  </> : <>
                                    <LockIcon className="w-4 h-4" data-id="element-8122" />
                                    <span data-id="element-8123">Blocat</span>
                                  </>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
          </div>
        </div>

        {isActive && detailedCourse && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mt-12" data-id="element-8124">
            <Link to={`/curs/${courseInfo.id}/modul/${detailedCourse.modules[0].id}`} className="inline-flex items-center space-x-2 bg-[#357BEB] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#2868d8] transition-all transform hover:scale-105 shadow-lg" data-id="element-8125">
              <span data-id="element-8126">Începe primul modul</span>
              <ArrowRightIcon className="w-5 h-5" data-id="element-8127" />
            </Link>
          </motion.div>}

        {isActive && !detailedCourse && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mt-12" data-id="element-8128">
            <div className="bg-blue-50 border-l-4 border-[#357BEB] p-6 rounded-r-lg inline-block" data-id="element-8129">
              <p className="text-[#132C53] font-medium" data-id="element-8130">
                📝 Conținutul detaliat al acestui training va fi adăugat în
                curând
              </p>
            </div>
          </motion.div>}
      </div>

      <LockedCourseModal isOpen={showLockedModal} onClose={() => setShowLockedModal(false)} courseTitle={selectedCourse} data-id="element-8131" />
    </div>;
}